import React, { useEffect, useState, useRef } from "react";
import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import { testApi } from "../../api/testApi";
import { CheckCircle, XCircle } from "lucide-react";
import { getTestErrorDisplay } from "../../utils/apiErrorMessage";
import testNotFound from "../../assets/no-test-found.png";

const CourseQuiz = ({
  testId,
  testTitle,
  courseId,
  onComplete,
  onBack,
  showRetakeOption = true,
  material,
  setInQuiz
}) => {
  const navigate = useNavigate();

  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Test flow state
  const [testAttempt, setTestAttempt] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [submittedAnswers, setSubmittedAnswers] = useState(new Set());
  const [testResults, setTestResults] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const hasStartedRef = useRef(false);

  // New state for answer feedback
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerFeedback, setAnswerFeedback] = useState(null);

  // Loading and error states
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);

  // Initialize test attempt when component mounts
  useEffect(() => {
    const startTestAttempt = async () => {
      if (!testId || hasStartedRef.current) return;
      hasStartedRef.current = true;

      try {
        setTestLoading(true);
        setTestError(null);

        const startResponse = await testApi.startTest({ testId });
        const attemptData = startResponse.data;

        setTestAttempt(attemptData);
        setAttemptId(attemptData.attemptId);

        // Transform options array to object format expected by the component
        const transformedQuestions = attemptData.questions.map((question) => ({
          ...question,
          options: question.options
            ? question.options.reduce((acc, option) => {
                acc[option.id] = option.text;
                return acc;
              }, {})
            : null,
        }));

        setQuestions(transformedQuestions);
        setQuestionStartTime(Date.now());
      } catch (error) {
        console.error("Failed to start test:", error);
        setTestError(error || { message: "Failed to start test." });
      } finally {
        setTestLoading(false);
      }
    };

    startTestAttempt();
  }, [testId]);

  const handleAnswerSelect = (questionId, answerValue) => {
    if (answerSubmitted) return; // Prevent changing answer after submission

    setAnswers((prev) => ({
      ...prev,
      [questionId]: answerValue,
    }));
  };

  const handleSubmitAnswer = async () => {
    const currentQ = questions[currentQuestion];
    const userAnswerId = answers[currentQ.id];

    if (!attemptId || !userAnswerId || answerSubmitted) return;

    try {
      setTestLoading(true);
      const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);

      // Find the option object to send as userAnswer
      const originalQuestion = testAttempt.questions.find(
        (q) => q.id === currentQ.id
      );
      const selectedOption = originalQuestion.options.find(
        (opt) => opt.id === userAnswerId
      );

      const submitResponse = await testApi.submitAnswer(attemptId, {
        questionId: currentQ.id,
        userAnswer: selectedOption,
        timeSpent,
      });

      const feedback = submitResponse.data;
      setAnswerFeedback(feedback);
      setAnswerSubmitted(true);

      // Mark this answer as submitted
      setSubmittedAnswers((prev) => new Set([...prev, currentQ.id]));
    } catch (error) {
      console.error("Failed to submit answer:", error);
    } finally {
      setTestLoading(false);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
      setQuestionStartTime(Date.now());
      setAnswerSubmitted(false);
      setAnswerFeedback(null);
    } else {
      // All questions answered, complete the test
      handleCompleteTest();
    }
  };

  const handleCompleteTest = async () => {
    if (!attemptId) return;

    try {
      setTestLoading(true);

      // Complete the test
      const completeResponse = await testApi.completeTest(attemptId);
      const results = completeResponse.data;

      setTestResults(results);
      setQuizCompleted(true);
    } catch (error) {
      console.error("Failed to complete test:", error);
      setTestError(error || { message: "Failed to complete test." });
    } finally {
      setTestLoading(false);
    }
  };

  const handleSubmitQuiz = () => {
    if (material?.nextMaterial?.id) {
      setInQuiz(false);
      navigate(`/dashboard/courses/${courseId}/${material?.nextMaterial?.id}`);
    } else {
      handleReturnToCourse();
    }
  };

  const handleReturnToCourse = () => {
    if (onComplete) {
      onComplete(testResults);
    } else if (courseId) {
      navigate(`/dashboard/courses/${courseId}`);
    } else if (onBack) {
      onBack();
    }
  };

  // Loading state
  if (testLoading && !testAttempt) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[var(--secondary-font)] mx-auto mb-4" />
          <p className="text-[var(--secondary-font)]">Starting Quiz...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (testError || !testAttempt) {
    const display = getTestErrorDisplay(testError);
    return (
      <div className="flex flex-col items-center justify-center w-full h-full p-6">
        <img
          src={testNotFound}
          alt="Test Not Found"
          className="w-32 h-32 opacity-90"
        />
        <div className="text-2xl font-bold text-[var(--secondary-font)] mb-2">
          {display.title}
        </div>
        <div className="text-gray-500 text-lg mb-4">
          {display.message}
        </div>
        <div className="flex items-center gap-2">
        <Button
          className="mt-2 px-8 py-2 rounded-full !border-[#2B6A7F] border-2 text-[#2B6A7F]"
          onClick={handleReturnToCourse}
          >
          Back to Course
        </Button>
        <Button
          className="mt-2 px-8 py-2 rounded-full bg-[var(--secondary-font)] text-white hover:bg-[var(--primary-font)]"
          onClick={handleSubmitQuiz}
          >
          Go to Next Material
        </Button>
          </div>
      </div>
    );
  }

  // Quiz Completion Screen
  if (quizCompleted) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center rounded-3xl p-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-[var(--secondary-font)] mb-4">
            Quiz Completed!
          </h3>
          <p className="text-lg text-[var(--default-font)] mb-8">
            You have answered all {questions.length} questions. Click below to 
            continue to the next material.
          </p>
          <Button
            onClick={handleSubmitQuiz}
            className="bg-[var(--secondary-font)] hover:bg-[var(--primary-font)] text-white px-8 py-2 rounded-full"
          >
            Go to Next Material
          </Button>
        </div>
      </div>
    );
  }

  // Main Quiz Interface
  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full p-6">
        <div className="text-2xl font-bold text-[var(--secondary-font)] mb-2">
          No Questions Available
        </div>
        <div className="text-gray-500 text-lg mb-4">
          This quiz doesn't have any questions yet.
        </div>
        <Button
          className="mt-2 px-8 py-2 rounded-full bg-[var(--secondary-font)] text-white hover:bg-[var(--primary-font)]"
          onClick={handleReturnToCourse}
        >
          Back to Course
        </Button>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const totalQuestions = questions.length;
  const selectedAnswer = answers[currentQ.id];

  return (
    <div className="w-full h-full bg-white rounded-3xl overflow-y-auto p-8 font-sans">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-bold text-[#2B6A7F]">
              Practice your learning!
            </h3>
            <p className="text-sm text-[#2B6A7F]">Not For Grading</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#00000099]">
              Question {currentQuestion + 1} of {totalQuestions}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h4 className="text-lg font-semibold text-black mb-6">
          Question: {currentQ.question}
        </h4>

        {/* Display media if available */}
        {currentQ.imageUrl && (
          <img
            src={currentQ.imageUrl}
            alt="Question image"
            className="max-w-full h-auto mb-4 rounded-lg"
          />
        )}

        {currentQ.videoUrl && (
          <video
            src={currentQ.videoUrl}
            controls
            className="max-w-full h-auto mb-4 rounded-lg"
          />
        )}

        <p className="text-[#00000080] mb-3">(Select one answer)</p>

        {/* Handle different question types */}
        {currentQ.type === "TRUE_FALSE" && (
          <div className="space-y-3">
            {["true", "false"].map((option) => {
              const optionValue = option === "true";
              const isSelected = selectedAnswer === optionValue;
              const isCorrect = answerFeedback?.correctAnswer?.id === option;
              const isUserWrong =
                answerSubmitted && isSelected && !answerFeedback?.isCorrect;

              let buttonClass =
                "w-full text-left p-4 rounded-full border-2 transition-all ";

              if (answerSubmitted) {
                if (isCorrect) {
                  buttonClass += "bg-green-100 border-green-400 text-green-800";
                } else if (isUserWrong) {
                  buttonClass += "bg-red-100 border-red-400 text-red-800";
                } else {
                  buttonClass += "border-gray-200 bg-gray-50";
                }
              } else {
                if (isSelected) {
                  buttonClass += "bg-blue-100 !border-[#2B6A7F]";
                } else {
                  buttonClass += "border-gray-200 hover:border-gray-300";
                }
              }

              return (
                <div key={option} className="relative">
                  <button
                    onClick={() => handleAnswerSelect(currentQ.id, optionValue)}
                    disabled={answerSubmitted}
                    className={buttonClass}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium capitalize">{option}</span>
                      {answerSubmitted && (
                        <div className="flex items-center gap-2">
                          {isSelected && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              Your Answer
                            </span>
                          )}
                          {isCorrect && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Correct
                            </span>
                          )}
                          {isUserWrong && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                              Incorrect
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {currentQ.type === "SINGLE_SELECT" && currentQ.options && (
          <div className="space-y-3">
            {Object.entries(currentQ.options).map(([key, text]) => {
              const isSelected = selectedAnswer === key;
              const isCorrect = answerFeedback?.correctAnswer?.id === key;
              const isUserWrong =
                answerSubmitted && isSelected && !answerFeedback?.isCorrect;

              let buttonClass =
                "w-full text-left p-4 rounded-xl border-2 transition-all ";

              if (answerSubmitted) {
                if (isCorrect) {
                  buttonClass += "bg-green-100 border-green-400 text-green-800";
                } else if (isUserWrong) {
                  buttonClass += "bg-red-100 border-red-400 text-red-800";
                } else {
                  buttonClass += "border-gray-200 bg-gray-50";
                }
              } else {
                if (isSelected) {
                  buttonClass += "bg-[#D9ECF8] !border-[#2B6A7F]";
                } else {
                  buttonClass += "border-gray-200 hover:border-gray-300";
                }
              }

              return (
                <div key={key} className="relative">
                  <button
                    onClick={() => handleAnswerSelect(currentQ.id, key)}
                    disabled={answerSubmitted}
                    className={buttonClass}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected
                              ? "border-[#2B6A7F] bg-[#2B6A7F]"
                              : "border-gray-400 bg-white"
                          }`}
                        >
                          {isSelected && (
                            <span className="w-2.5 h-2.5 rounded-full bg-white" />
                          )}
                        </span>
                        {text}
                      </div>
                      {answerSubmitted && (
                        <div className="flex items-center gap-2">
                          {isSelected && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              Your Answer
                            </span>
                          )}
                          {isCorrect && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Correct
                            </span>
                          )}
                          {isUserWrong && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                              Incorrect
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        {!answerSubmitted ? (
          <Button
            onClick={handleSubmitAnswer}
            disabled={selectedAnswer === undefined || testLoading}
            className="bg-[var(--secondary-font)] hover:bg-[var(--primary-font)] text-white px-8 py-2 rounded-full"
          >
            {testLoading ? "Submitting..." : "Submit"}
          </Button>
        ) : (
          <Button
            onClick={handleNextQuestion}
            className="bg-[var(--secondary-font)] hover:bg-[var(--primary-font)] text-white px-8 py-2 rounded-full"
          >
            {currentQuestion === totalQuestions - 1 ? "Finish" : "Next"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CourseQuiz;
